import { createOperationDescriptor } from 'relay-runtime';
import type { IEnvironment } from 'relay-runtime';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import SpaceNavigationQueryArtifact, {
	type SpaceNavigationQuery$data,
	type SpaceNavigationQuery$variables,
} from './SpaceNavigation/__generated__/SpaceNavigationQuery.graphql.ts';
import {
	getOrCreateSpaceNavigationQueryOwner,
	getSpaceNavigationQueryOwner,
} from './SpaceNavigation/SpaceNavigationQueryOwner.ts';

export type NavigationPreloadVariables = Pick<
	SpaceNavigationQuery$variables,
	| 'contentId'
	| 'includeBlogTree'
	| 'includeHomepageTreeVariant'
	| 'includePageTree'
	| 'isSuperAdmin'
	| 'paginationLimit'
	| 'ptStatuses'
>;

/** Every route transition can cancel unclaimed navigation work, including routes without a sidebar. */
export function cancelSpaceNavigationQueryPreload(): void {
	getSpaceNavigationQueryOwner(getRelayEnvironment())?.beginPreload();
}

/** Reserve the handoff before async route inputs are read, so cancelled routes cannot win late. */
export function createSpaceNavigationQueryPreloader(): (
	spaceKey: string,
	navigationVariables: NavigationPreloadVariables,
) => Promise<{ data: SpaceNavigationQuery$data | null; cancelled?: boolean }> {
	const environment = getRelayEnvironment();
	const context = getPreloaderFnContext();
	const owner = getOrCreateSpaceNavigationQueryOwner(environment);
	const generation = owner.beginPreload();
	return (spaceKey: string, navigationVariables: NavigationPreloadVariables) =>
		owner.isCurrentPreload(generation)
			? preloadSpaceNavigationQuery(spaceKey, navigationVariables, environment, context)
			: Promise.resolve({ data: null, cancelled: true });
}

/** The caller gates this owner with the contract experiment and query-owner killswitch. */
function preloadSpaceNavigationQuery(
	spaceKey: string,
	navigationVariables: NavigationPreloadVariables,
	environment: IEnvironment,
	context: ReturnType<typeof getPreloaderFnContext>,
): Promise<{ data: SpaceNavigationQuery$data | null; cancelled?: boolean }> {
	const { cloudId, edition, isLicensed, isLoggedIn, accessStatus } = context;
	const hasLicensedAccess =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;
	const variables: SpaceNavigationQuery$variables = {
		cloudId,
		spaceKey,
		isLicensed,
		includePersistentInviteLicense:
			edition !== ConfluenceEdition.FREE &&
			isLoggedIn &&
			(!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_license_error_fix')
				? hasLicensedAccess
				: isLicensed),
		includeSpaceViewsPersistence: isLicensed,
		includeSharedSpaceDetails:
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_shared_space_frag_reader') &&
			expVal('confluence_exp_space_nav_contract', 'isEnabled', false),
		includeSpaceCreationEligibility:
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_creation_frag_reader') &&
			expVal('confluence_exp_space_nav_contract', 'isEnabled', false),
		...(!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_trees_frag_reader') &&
		expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
			? navigationVariables
			: {}),
	};
	const reference = getOrCreateSpaceNavigationQueryOwner(environment).preload(variables);
	return reference.ready.then(() => ({
		cancelled: reference.disposed,
		data:
			reference.disposed || reference.failed
				? null
				: (environment.lookup(
						createOperationDescriptor(SpaceNavigationQueryArtifact, reference.variables).fragment,
					).data as SpaceNavigationQuery$data),
	}));
}
